import ModalDialog, { ModalBody } from '@atlaskit/modal-dialog';
import React, { useCallback, useEffect, useMemo } from 'react';

import { createPlatformSiteAri } from '@townsquare/collab-context/create-site-ari';
import { useFlagActions } from '@townsquare/flags';
import { useModalActions } from '@townsquare/modals';
import { noop } from '@townsquare/noop';
import { expVal } from '@townsquare/stat-sig';
import { JIRA_SPACE_INVITE_TEAMS_APP } from '@townsquare/stat-sig/experiments';

import InvitePeople from './components/AkInvitePeople';
import { useOnSubmit } from './hooks/useOnSubmit';
import { useSpaceInviteInput } from './hooks/useSpaceInviteInput';
import { useInviteUserStore } from './store';

export const useInviteUserToSiteModal = ({
	cloudId,
	analyticsSource,
	enabled = true,
}: {
	cloudId: string | null | undefined;
	analyticsSource: string;
	/**
	 * Defaults to true
	 */
	enabled?: boolean;
}) => {
	const { openModal, closeModal } = useModalActions();
	const [{ canInviteUserToSite }, { checkAccessToInviteUserToSite }] = useInviteUserStore();

	useEffect(() => {
		if (!cloudId || !enabled) {
			return;
		}
		checkAccessToInviteUserToSite(cloudId);
	}, [checkAccessToInviteUserToSite, cloudId, enabled]);

	const handleOpen = useCallback(() => {
		openModal('invite-user-to-site-modal', {
			component: expVal(JIRA_SPACE_INVITE_TEAMS_APP, 'isEnabled', false) ? (
				<InviteToSiteModalWithSpaceInviteExperiment
					analyticsSource={analyticsSource}
					cloudId={cloudId}
					onCancel={() => closeModal('invite-user-to-site-modal')}
				/>
			) : (
				<InviteToSiteModal
					analyticsSource={analyticsSource}
					cloudId={cloudId}
					onCancel={() => closeModal('invite-user-to-site-modal')}
				/>
			),
		});
	}, [openModal, analyticsSource, cloudId, closeModal]);

	return {
		openInviteUserToSiteModal: canInviteUserToSite ? handleOpen : noop,
		canInviteUserToSite,
	};
};

type Props = {
	onCancel: () => void;
	cloudId: string | null | undefined;
	analyticsSource: string;
};

const InviteToSiteModal = ({ onCancel, cloudId, analyticsSource }: Props) => {
	const { addFlag } = useFlagActions();

	const onClose = () => {
		onCancel();
	};

	if (!cloudId) {
		return null;
	}

	return (
		<ModalDialog onClose={onClose} width="small">
			<ModalBody>
				<InvitePeople
					resourceAri={`ari:cloud:platform::site/${cloudId}`}
					onCancelHandler={onClose}
					showFlag={flag => addFlag(flag)}
					analyticsContext={{
						source: analyticsSource,
					}}
					onSendHandler={onClose}
					source={analyticsSource}
				/>
			</ModalBody>
		</ModalDialog>
	);
};

const InviteToSiteModalWithSpaceInviteExperiment = ({ onCancel, cloudId, analyticsSource }: Props) => {
	const { addFlag } = useFlagActions();
	const spaceInviteInput = useSpaceInviteInput({ cloudId });
	const onSubmit = useOnSubmit(
		spaceInviteInput.data.selectedSpace,
		spaceInviteInput.data.selectedRole,
		cloudId,
		onCancel,
	);

	const plugins = useMemo(
		() => ({
			footer: spaceInviteInput.plugin,
		}),
		[spaceInviteInput.plugin],
	);

	if (!cloudId) {
		return null;
	}

	return (
		<ModalDialog onClose={onCancel} width="small">
			<ModalBody>
				<InvitePeople
					resourceAri={createPlatformSiteAri(cloudId) ?? ''}
					onCancelHandler={onCancel}
					showFlag={flag => addFlag(flag)}
					analyticsContext={{
						source: analyticsSource,
					}}
					onSendHandler={onCancel}
					source={analyticsSource}
					plugins={spaceInviteInput.plugin ? plugins : undefined}
					onSubmit={onSubmit}
				/>
			</ModalBody>
		</ModalDialog>
	);
};
